/**
 * El plan de ejecución, convertido en recorrido.
 *
 * El informe termina en un plan a 7, 30 y 90 días, y hasta ahora eso era un
 * bloque de texto al final de veintinueve secciones — donde nadie vuelve. Aquí
 * es una pantalla propia con una estación por tramo.
 *
 * **Lo que está bajo llave es la herramienta, nunca el hito.** Quien no ha
 * pagado lee entero qué tiene que hacer, puede marcarlo como hecho y ve su
 * avance. Lo que no tiene es la plantilla o el guion que se lo hace más rápido.
 * Por eso las herramientas bloqueadas dicen para qué sirven en vez de taparse
 * con un difuminado: si no se entiende qué te estás perdiendo, no hay razón
 * para pagarlo — y si no merece la pena, tampoco debería venderse.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Logo } from "../components/Logo";
import { EntradaAlMapa } from "../components/EntradaAlMapa";
import { BarraSesion } from "../components/Sesion";
import api, { type Estacion, type PlanDeTrabajo } from "../lib/sessions";
import { PRODUCT_COLOR } from "../lib/theme";

/** El producto que abre cada nivel, con lo que hay que decir para venderlo. */
const PRODUCTO: Record<string, { nombre: string; precio: string; color: string }> = {
  system: { nombre: "System", precio: "69 €", color: PRODUCT_COLOR.system },
  sprint: { nombre: "Sprint", precio: "197 €", color: PRODUCT_COLOR.sprint },
  diagnostic: { nombre: "Diagnostic", precio: "297 €", color: PRODUCT_COLOR.diagnostic },
};

export default function Plan() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [datos, fijarDatos] = useState<PlanDeTrabajo | null>(null);
  const [error, fijarError] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    api.plan(sessionId).then(fijarDatos, () => fijarError(true));
  }, [sessionId]);

  // La marca se aplica sobre lo que ya hay en pantalla y luego se confirma
  // contra el servidor. Esperar a la respuesta para tachar una línea se nota, y
  // lo que está en juego es una casilla: si falla, se devuelve donde estaba.
  const marcar = async (clave: string, hecha: boolean) => {
    if (!sessionId || !datos) return;
    const antes = datos;
    fijarDatos(conAccion(datos, clave, hecha));
    try {
      await api.marcarAccion(sessionId, clave, hecha);
    } catch {
      fijarDatos(antes);
    }
  };

  if (error) return <Pantalla>No se ha podido cargar tu plan.</Pantalla>;
  if (!datos) return <Pantalla>Cargando tu plan…</Pantalla>;

  const total = datos.estaciones.reduce((n, e) => n + e.acciones.length, 0);
  const hechas = datos.estaciones.reduce(
    (n, e) => n + e.acciones.filter((a) => a.hecha).length,
    0,
  );

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-line bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-4">
          <Logo />
          <BarraSesion />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="font-display text-3xl text-ink">Tu plan, paso a paso</h1>
        <p className="mt-2 max-w-prose text-ink/70">
          Lo que tu diagnóstico dice que tienes que hacer, repartido en tres tramos. Marca
          lo que vayas cerrando: nadie más que tú ve esto.
        </p>

        {total > 0 ? (
          <p className="mt-6 text-sm tabular-nums text-ink/60">
            {hechas} de {total} acciones completadas
          </p>
        ) : null}

        <ol className="mt-8 space-y-10">
          {datos.estaciones.map((e, i) => (
            <EstacionVista
              key={e.tramo}
              estacion={e}
              numero={i + 1}
              ultima={i === datos.estaciones.length - 1}
              marcar={marcar}
            />
          ))}
        </ol>

        <div className="mt-12">
          <EntradaAlMapa />
        </div>
      </main>
    </div>
  );
}

function EstacionVista({
  estacion,
  numero,
  ultima,
  marcar,
}: {
  estacion: Estacion;
  numero: number;
  ultima: boolean;
  marcar: (clave: string, hecha: boolean) => void;
}) {
  const completa =
    estacion.acciones.length > 0 && estacion.acciones.every((a) => a.hecha);

  return (
    <li className="relative pl-10">
      {/* La línea que une las estaciones: es lo que hace que se lea como un
          recorrido y no como tres listas seguidas. */}
      {!ultima ? (
        <span
          aria-hidden="true"
          className="absolute left-[15px] top-9 h-[calc(100%-1rem)] w-px bg-paper-2"
        />
      ) : null}

      <span
        aria-hidden="true"
        className={
          "absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full border text-sm tabular-nums " +
          (completa
            ? "border-ink bg-ink text-paper"
            : "border-line bg-white text-teal")
        }
      >
        {completa ? "✓" : numero}
      </span>

      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-h3 font-medium text-ink">{estacion.titulo}</h2>
        <span className="text-sm text-ink/50">{estacion.plazo}</span>
      </div>
      {/* critero-lint-disable-next-line proposito: nombre del campo en la API; se ve su contenido. */}
      <p className="mt-2 max-w-prose text-ink/70">{estacion.proposito}</p>

      {estacion.acciones.length > 0 ? (
        <ul className="mt-5 space-y-2">
          {estacion.acciones.map((a) => (
            <li key={a.clave}>
              <label className="flex cursor-pointer items-start gap-3 rounded-panel border border-line bg-white p-3 transition hover:border-line">
                <input
                  type="checkbox"
                  checked={a.hecha}
                  onChange={(ev) => marcar(a.clave, ev.target.checked)}
                  className="mt-1 h-4 w-4 accent-ink"
                />
                <span className={a.hecha ? "text-ink/45 line-through" : "text-ink"}>
                  {a.texto}
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}

      {estacion.herramientas.length > 0 ? (
        <div className="mt-5 space-y-3">
          {estacion.herramientas.map((h) => (
            <HerramientaVista key={h.clave} h={h} />
          ))}
        </div>
      ) : null}
    </li>
  );
}

function HerramientaVista({
  h,
}: {
  h: { nombre: string; para_que: string; formato: string; nivel: string; abierta: boolean };
}) {
  const p = PRODUCTO[h.nivel];

  return (
    <div
      className={
        "rounded-tarjeta border p-4 " +
        (h.abierta ? "border-line bg-white" : "border-line bg-paper-2")
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-body font-medium text-ink">{h.nombre}</h3>
        {h.abierta ? null : (
          <span
            className="text-xs font-medium uppercase tracking-wider"
            style={{ color: p?.color }}
          >
            {p ? `${p.nombre} · ${p.precio}` : "De pago"}
          </span>
        )}
      </div>

      {/* Para qué sirve se lee esté abierta o no: es lo que justifica pagarla. */}
      <p className="mt-2 text-sm leading-relaxed text-ink/70">{h.para_que}</p>
      <p className="mt-1 text-xs text-ink/45">{h.formato}</p>

      {h.abierta ? (
        <button
          type="button"
          className="mt-4 critero-boton px-4 py-2"
        >
          Abrir herramienta
        </button>
      ) : (
        <p className="mt-3 text-xs text-ink/50">
          Se desbloquea con {p?.nombre ?? "un producto de pago"}. El diagnóstico y este
          plan seguirán siendo tuyos y gratuitos.
        </p>
      )}
    </div>
  );
}

/** Devuelve el plan con una acción cambiada, sin mutar el anterior. */
function conAccion(v: PlanDeTrabajo, clave: string, hecha: boolean): PlanDeTrabajo {
  return {
    ...v,
    estaciones: v.estaciones.map((e) => ({
      ...e,
      acciones: e.acciones.map((a) => (a.clave === clave ? { ...a, hecha } : a)),
    })),
  };
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6">
      <p className="text-ink/60">{children}</p>
    </div>
  );
}
